import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { LoadingSpinner } from "../LoadingSpinner";
import MainContainer from "../container/MainContainer";
import { useUser } from "../../context/UserContext";
import { isWaterSystemAcknowledged } from "../../services/PWSServices";
import { logger } from "../../utility/logger";
import { WATER_SYSTEMS } from "../../utility/routingInformation";

const PWSForUsers = () => {
  const { group, userInfo } = useUser();
  const { user_email } = userInfo;

  const params = useParams();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  // only WATER_SYSTEMS group can access the water system from this route
  const [isAuthorizedUser, setIsAuthorizedUser] = useState(
    WATER_SYSTEMS.some((item) => group.includes(item))
  );

  // Extract PWS ID from either acknowledgement route or regular route
  const { pwsId, pwsKey } = params;

  useEffect(() => {
    const checkAcknowledgementStatus = async () => {
      // incase if not pwsId found or an unauthorized user trying to access
      // don't check with backend. return the user to dashboard
      if (!pwsKey || !isAuthorizedUser) {
        logger.log(
          "Not able to find the pwsKey from the URL or the user is not authorized"
        );
        setIsLoading(false);
        return;
      }

      const response = await isWaterSystemAcknowledged(user_email, +pwsKey);
      const { status, data } = response;
      if (status === 1) {
        setIsAcknowledged(data.is_acknowledged);
      } else {
        const { message } = response;
        if (message === "You do not have access to this PWS")
          setIsAuthorizedUser(false);

        logger.error(
          "Unable to verify the water system acknowledgement with backend"
        );
      }
      setIsLoading(false);
    };

    checkAcknowledgementStatus();
  }, [pwsKey]);

  // First check: User group authorization
  if (!isAuthorizedUser) return <Navigate to="/dashboard" replace={true} />;

  // when verifying the acknowledgement status
  if (isLoading) {
    return (
      <MainContainer>
        <LoadingSpinner size="40px" className="h-[223px]" />
      </MainContainer>
    );
  }

  // pwsId doesn't exist : not a valid route
  if (!pwsKey) return <Navigate to="/dashboard" replace={true} />;

  // Check if we're already on the acknowledgement page to prevent redirect loops
  const isAcknowledgementPage = location.pathname.includes(
    `/acknowledgement/${pwsId}`
  );

  // If not acknowledged and not on acknowledgement page, redirect to acknowledgement
  if (!isAcknowledged && !isAcknowledgementPage) {
    logger.log(
      "User is not acknowledged for this water system. Redirecting to acknowledgement page"
    );
    return (
      <Navigate
        to={`/acknowledgement/${pwsId}/${pwsKey}`}
        replace={true}
        state={{ comingFromValidPath: true }}
      />
    );
  }

  // If on acknowledgement page but system is acknowledged, redirect to dashboard
  if (isAcknowledged && isAcknowledgementPage)
    return <Navigate to="/dashboard" replace={true} />;

  // If all checks pass, render the protected content
  return <Outlet />;
};

export default PWSForUsers;
